import React, { useState, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Settings, Globe } from 'lucide-react';
import { getCpaClassName, getCosClassName, getMetricObjectiveClassName, useComponentMetrics, panierMoyenPublicitaire } from '../../utils/metricsUtils';
import MetricsConfigModal from '../MetricsConfigModal';
import ModuleVisibilityToggle from '../ModuleVisibilityToggle';

import { calculateVariation, renderVariation as renderVariationBase } from '../../utils/variationUtils';
/**
 * Composant table des réseaux Microsoft Ads avec graphiques
 * Affiche les performances par type de réseau (Recherche AOL, Audience, Sites Microsoft, Partenaires syndiqués)
 * Utilise les données du dashboard principal pour garantir des totaux cohérents
 */
const NetworksTable = React.memo(({
  selectedAccount = null,
  formatNumber,
  isVisible = true,
  COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8'],
  selectedCampaignFilter = null,
  networkData = [],
  networkDataByCampaign = {},
  // Props pour le filtrage global par réseau
  onNetworkClick = null,
  selectedNetworkFilter = null,
  // Props pour la comparaison
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = '',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'msads_networks'
}) => {
  // États locaux
  const [showSection, setShowSection] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // Hook pour les métriques du composant (réactif aux changements)
  const componentMetrics = useComponentMetrics('msads_networks', selectedAccount?.id || 'global');

  // Fonction helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);


  // Helper pour afficher la variation
  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Données de comparaison par réseau
  const previousNetworksMap = useMemo(() => {
    if (!compareEnabled || !previousPeriodData?.networks) return {};
    const map = {};
    previousPeriodData.networks.forEach(network => {
      // Utiliser networkName (nom traduit) comme clé prioritaire pour le matching
      const key = network.networkName || network.name || network.network;
      if (key) map[key] = network;
    });
    return map;
  }, [compareEnabled, previousPeriodData?.networks]);

  // Données à afficher (filtrées par campagne si nécessaire)
  const filteredData = useMemo(() => {
    if (!selectedCampaignFilter) {
      return networkData;
    }

    const campaignName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName;
    if (campaignName && networkDataByCampaign[campaignName]) {
      return networkDataByCampaign[campaignName];
    }

    return networkData;
  }, [networkData, networkDataByCampaign, selectedCampaignFilter]);

  // Tri des données
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = useMemo(() => {
    if (!filteredData.length) return [];

    const sortableData = [...filteredData];

    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        // Le panier moyen n'est pas un champ des lignes : il se calcule, ici comme à l'affichage.
        // Le trier comme les autres aurait comparé des zéros et rendu un ordre arbitraire.
        const aVal = sortConfig.key === 'panier_moyen' ? panierMoyenPublicitaire(a) : (a[sortConfig.key] || 0);
        const bVal = sortConfig.key === 'panier_moyen' ? panierMoyenPublicitaire(b) : (b[sortConfig.key] || 0);

        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return sortableData;
  }, [filteredData, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return null;
    }
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Calculer les totaux
  const totals = useMemo(() => {
    return filteredData.reduce((acc, network) => ({
      impressions: (acc.impressions || 0) + (network.impressions || 0),
      clicks: (acc.clicks || 0) + (network.clicks || 0),
      cost: (acc.cost || 0) + (network.cost || 0),
      conversions: (acc.conversions || 0) + (network.conversions || 0),
      conversionsValue: (acc.conversionsValue || 0) + (network.conversionsValue || 0)
    }), {});
  }, [filteredData]);

  const totalMetrics = useMemo(() => {
    return {
      ctr: totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100) : 0,
      cpc: totals.clicks > 0 ? (totals.cost / totals.clicks) : 0,
      conversionRate: totals.clicks > 0 ? ((totals.conversions / totals.clicks) * 100) : 0,
      cpa: totals.conversions > 0 ? (totals.cost / totals.conversions) : 0,
      cos: totals.conversionsValue > 0 ? ((totals.cost / totals.conversionsValue) * 100) : 0
    };
  }, [totals]);

  // Données pour les graphiques
  const clicksChartData = useMemo(() => {
    if (!filteredData.length) return [];

    return filteredData
      .map((network, index) => ({
        name: network.networkName,
        value: network.clicks || 0,
        percentage: totals.clicks > 0 ? ((network.clicks / totals.clicks) * 100).toFixed(1) : 0,
        color: COLORS[index % COLORS.length]
      }))
      .filter(network => parseFloat(network.percentage) > 0.0);
  }, [filteredData, totals.clicks, COLORS]);

  const conversionsChartData = useMemo(() => {
    if (!filteredData.length) return [];

    return filteredData
      .map((network, index) => ({
        name: network.networkName,
        value: network.conversions || 0,
        percentage: totals.conversions > 0 ? ((network.conversions / totals.conversions) * 100).toFixed(1) : 0,
        color: COLORS[index % COLORS.length]
      }))
      .filter(network => parseFloat(network.percentage) > 0.0);
  }, [filteredData, totals.conversions, COLORS]);

  // Couleur par réseau, fixée sur l'ordre de `filteredData`. Le tableau parcourt
  // `sortedData` : sans cette table, la pastille d'une ligne changeait de couleur
  // au tri d'une colonne et ne correspondait plus à la part du camembert.
  const networkColors = useMemo(() => {
    const map = {};
    filteredData.forEach((network, index) => {
      map[network.networkName] = COLORS[index % COLORS.length];
    });
    return map;
  }, [filteredData, COLORS]);

  // Données pour la légende
  const legendData = useMemo(() => {
    if (!filteredData.length) return [];

    return filteredData.map((network, index) => ({
      value: network.networkName,
      type: 'rect',
      color: COLORS[index % COLORS.length]
    }));
  }, [filteredData, COLORS]);

  if (!isVisible) return null;

  // Ne pas afficher le conteneur s'il n'y a pas de données
  if (!filteredData.length) return null;

  // Vérification de visibilité pour les clients
  const clientModuleVisible = isClientModuleVisible ? isClientModuleVisible(moduleId) : true;
  if (userRole === 'CLIENT' && !clientModuleVisible) {
    return null;
  }

  return (
    <div className="networks-section campaigns-table-section">
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><Globe size={20} /> Réseaux</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {selectedNetworkFilter && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              backgroundColor: '#e0f2fe',
              borderRadius: '6px',
              fontSize: '12px',
              color: '#0369a1'
            }}>
              <span>Filtré : <strong>{selectedNetworkFilter.networkName}</strong></span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNetworkClick && onNetworkClick(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#0369a1',
                  fontSize: '14px',
                  padding: '0 2px'
                }}
                title="Réinitialiser le filtre"
              >
                ✕
              </button>
            </div>
          )}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={clientModuleVisible}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Réseaux Microsoft Ads"
            />
          ) : (
            <button
              className="section-toggle-btn"
              onClick={() => setShowSection(!showSection)}
              title={showSection ? "Masquer la section" : "Afficher la section"}
            >
              {showSection ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {(userRole !== 'CLIENT' || clientModuleVisible) && showSection && filteredData.length > 0 && (
        <>
          {/* Graphiques en secteurs côte à côte */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Pie chart Clics (gauche) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Clics par réseau</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={clicksChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {clicksChartData.map((entry, index) => (
                            <Cell key={`clicks-cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' clics', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Légende commune (centre) */}
                  <div style={{
                    minWidth: '180px',
                    padding: '0 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    {legendData.map((item, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                        fontSize: '12px'
                      }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          backgroundColor: item.color,
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}></div>
                        <span>{item.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Pie chart Conversions (droite) */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Conversions par réseau</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={conversionsChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {conversionsChartData.map((entry, index) => (
                            <Cell key={`conversions-cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ' conversions', name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
            </div>
          </div>

          {/* Tableau détaillé des réseaux */}
          <div className="device-table-wrapper">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('networkName')} className="sortable" style={{ width: '220px', maxWidth: '220px' }}>
                    Réseau {sortConfig.key === 'networkName' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {isComponentMetricActive('impressions') && (
                    <th onClick={() => handleSort('impressions')} className="sortable numeric">
                      Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('ctr') && (
                    <th onClick={() => handleSort('ctr')} className="sortable numeric">
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('clics') && (
                    <th onClick={() => handleSort('clicks')} className="sortable numeric">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpc') && (
                    <th onClick={() => handleSort('cpc')} className="sortable numeric">
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cout') && (
                    <th onClick={() => handleSort('cost')} className="sortable numeric">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('taux_conv') && (
                    <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                      Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('conv') && (
                    <th onClick={() => handleSort('conversions')} className="sortable numeric">
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cpa') && (
                    <th onClick={() => handleSort('cpa')} className="sortable numeric">
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('panier_moyen') && (
                    <th onClick={() => handleSort('panier_moyen')} className="sortable numeric">
                      Panier moyen {sortConfig.key === 'panier_moyen' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('valeur_conv') && (
                    <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                      Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('cos') && (
                    <th onClick={() => handleSort('cos')} className="sortable numeric">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roas') && (
                    <th onClick={() => handleSort('roas')} className="sortable numeric">
                      ROAS {sortConfig.key === 'roas' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricActive('roi') && (
                    <th onClick={() => handleSort('roi')} className="sortable numeric">
                      ROI {sortConfig.key === 'roi' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {sortedData.map((network, index) => {
                  const isSelected = selectedNetworkFilter?.networkCode === network.networkCode;
                  // Trouver le réseau correspondant dans les données de la période précédente
                  const prevNetwork = previousNetworksMap[network.networkName] || previousNetworksMap[network.network] || {};
                  const hasComparison = compareEnabled && Object.keys(prevNetwork).length > 0;

                  return (
                  <tr
                    key={`${network.networkCode}-${index}`}
                    onClick={() => onNetworkClick && onNetworkClick(isSelected ? null : network)}
                    style={{
                      cursor: onNetworkClick ? 'pointer' : 'default',
                      backgroundColor: isSelected ? '#e0f2fe' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    className={isSelected ? 'selected-row' : ''}
                  >
                    <td className="network-name" style={{ width: '220px', maxWidth: '220px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <span
                          className="network-indicator"
                          style={{
                            display: 'inline-block',
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: networkColors[network.networkName] || COLORS[index % COLORS.length],
                            marginRight: '8px',
                            marginTop: '4px',
                            flexShrink: 0
                          }}
                        ></span>
                        <span style={{ wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.3' }}>{network.networkName}</span>
                        {isSelected && <span style={{ marginLeft: '8px', color: '#0284c7' }}>✓</span>}
                      </div>
                    </td>
                    {isComponentMetricActive('impressions') && (
                      <td className="numeric">
                        {formatNumber(network.impressions || 0)}
                        {hasComparison && renderVariation(calculateVariation(network.impressions, prevNetwork.impressions))}
                      </td>
                    )}
                    {isComponentMetricActive('ctr') && (
                      <td className="numeric">
                        {formatNumber(network.ctr || 0, 2)}%
                        {hasComparison && renderVariation(calculateVariation(network.ctr, prevNetwork.ctr))}
                      </td>
                    )}
                    {isComponentMetricActive('clics') && (
                      <td className="numeric">
                        {formatNumber(network.clicks || 0)}
                        {hasComparison && renderVariation(calculateVariation(network.clicks, prevNetwork.clicks))}
                      </td>
                    )}
                    {isComponentMetricActive('cpc') && (
                      <td className="numeric">
                        {formatNumber(network.cpc || 0, 2)} €
                        {hasComparison && renderVariation(calculateVariation(network.cpc, prevNetwork.cpc), true)}
                      </td>
                    )}
                    {isComponentMetricActive('cout') && (
                      <td className="numeric">
                        {formatNumber(network.cost || 0, 2)} €
                        {hasComparison && renderVariation(calculateVariation(network.cost, prevNetwork.cost))}
                      </td>
                    )}
                    {isComponentMetricActive('taux_conv') && (
                      <td className="numeric">
                        {formatNumber(network.conversionRate || 0, 2)}%
                        {hasComparison && renderVariation(calculateVariation(network.conversionRate, prevNetwork.conversionRate))}
                      </td>
                    )}
                    {isComponentMetricActive('conv') && (
                      <td className="numeric">
                        {formatNumber(network.conversions || 0)}
                        {hasComparison && renderVariation(calculateVariation(network.conversions, prevNetwork.conversions))}
                      </td>
                    )}
                    {isComponentMetricActive('cpa') && (
                      <td className={`numeric ${getCpaClassName(network.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                        {network.cpa >= 0 ? `${formatNumber(network.cpa, 2)} €` : '-'}
                        {hasComparison && renderVariation(calculateVariation(network.cpa, prevNetwork.cpa), true)}
                      </td>
                    )}
                    {isComponentMetricActive('panier_moyen') && (
                      <td className="numeric">
                        {formatNumber(panierMoyenPublicitaire(network), 2)} €
                        {hasComparison && renderVariation(calculateVariation(panierMoyenPublicitaire(network), panierMoyenPublicitaire(prevNetwork)))}
                      </td>
                    )}
                    {isComponentMetricActive('valeur_conv') && (
                      <td className="numeric">
                        {formatNumber(network.conversionsValue || 0, 2)} €
                        {hasComparison && renderVariation(calculateVariation(network.conversionsValue, prevNetwork.conversionsValue))}
                      </td>
                    )}
                    {isComponentMetricActive('cos') && (
                      <td className={`numeric ${getCosClassName(network.cos || 0, selectedAccount?.id || 'global', moduleId)}`}>
                        {network.conversionsValue >= 0 ? formatNumber(network.cos || 0, 1) + '%' : '-'}
                        {hasComparison && renderVariation(calculateVariation(network.cos, prevNetwork.cos), true)}
                      </td>
                    )}
                    {isComponentMetricActive('roas') && (() => {
                      const roas = network.cost > 0 ? (network.conversionsValue / network.cost) * 100 : 0;
                      const prevRoas = prevNetwork.cost > 0 ? (prevNetwork.conversionsValue / prevNetwork.cost) * 100 : 0;
                      return (
                        <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                          {roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}
                          {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
                        </td>
                      );
                    })()}
                    {isComponentMetricActive('roi') && (() => {
                      const hasData = network.cost > 0 && network.conversionsValue > 0;
                      const roi = hasData ? (network.conversionsValue - network.cost) / network.cost : 0;
                      const prevHasData = prevNetwork.cost > 0 && prevNetwork.conversionsValue > 0;
                      const prevRoi = prevHasData ? (prevNetwork.conversionsValue - prevNetwork.cost) / prevNetwork.cost : 0;
                      return (
                        <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                          {formatNumber(roi, 2)}
                          {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
                        </td>
                      );
                    })()}
                  </tr>
                  );
                })}
              </tbody>

              <tfoot>
                {(() => {
                  // Calculer les totaux de la période précédente
                  const prevNetworks = previousPeriodData?.networks || [];
                  const prevTotals = prevNetworks.reduce((acc, network) => ({
                    impressions: (acc.impressions || 0) + (network.impressions || 0),
                    clicks: (acc.clicks || 0) + (network.clicks || 0),
                    cost: (acc.cost || 0) + (network.cost || 0),
                    conversions: (acc.conversions || 0) + (network.conversions || 0),
                    conversionsValue: (acc.conversionsValue || 0) + (network.conversionsValue || 0)
                  }), {});

                  const prevTotalMetrics = {
                    ctr: prevTotals.impressions > 0 ? ((prevTotals.clicks / prevTotals.impressions) * 100) : 0,
                    cpc: prevTotals.clicks > 0 ? (prevTotals.cost / prevTotals.clicks) : 0,
                    conversionRate: prevTotals.clicks > 0 ? ((prevTotals.conversions / prevTotals.clicks) * 100) : 0,
                    cpa: prevTotals.conversions > 0 ? (prevTotals.cost / prevTotals.conversions) : 0,
                    cos: prevTotals.conversionsValue > 0 ? ((prevTotals.cost / prevTotals.conversionsValue) * 100) : 0
                  };

                  return (
                    <tr className="totals-row" style={{
                      fontWeight: 'bold',
                      borderTop: '2px solid #ddd',
                      backgroundColor: 'white'
                    }}>
                      <td><strong>TOTAL</strong></td>
                      {isComponentMetricActive('impressions') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.impressions)}</strong>
                          {renderVariation(calculateVariation(totals.impressions, prevTotals.impressions || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('ctr') && (
                        <td className="numeric">
                          <strong>{formatNumber(totalMetrics.ctr, 2)}%</strong>
                          {renderVariation(calculateVariation(totalMetrics.ctr, prevTotalMetrics.ctr))}
                        </td>
                      )}
                      {isComponentMetricActive('clics') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.clicks)}</strong>
                          {renderVariation(calculateVariation(totals.clicks, prevTotals.clicks || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('cpc') && (
                        <td className="numeric">
                          <strong>{formatNumber(totalMetrics.cpc, 2)} €</strong>
                          {renderVariation(calculateVariation(totalMetrics.cpc, prevTotalMetrics.cpc), true)}
                        </td>
                      )}
                      {isComponentMetricActive('cout') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.cost, 2)} €</strong>
                          {renderVariation(calculateVariation(totals.cost, prevTotals.cost || 0), true)}
                        </td>
                      )}
                      {isComponentMetricActive('taux_conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(totalMetrics.conversionRate, 2)}%</strong>
                          {renderVariation(calculateVariation(totalMetrics.conversionRate, prevTotalMetrics.conversionRate))}
                        </td>
                      )}
                      {isComponentMetricActive('conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.conversions)}</strong>
                          {renderVariation(calculateVariation(totals.conversions, prevTotals.conversions || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('cpa') && (
                        <td className={`numeric ${getCpaClassName(totalMetrics.cpa || 0, selectedAccount?.id || 'global', moduleId)}`}>
                          <strong>{totalMetrics.cpa >= 0 ? `${formatNumber(totalMetrics.cpa, 2)} €` : '-'}</strong>
                          {renderVariation(calculateVariation(totalMetrics.cpa, prevTotalMetrics.cpa), true)}
                        </td>
                      )}
                      {isComponentMetricActive('panier_moyen') && (
                        <td className="numeric">
                          <strong>{formatNumber(panierMoyenPublicitaire(totals), 2)} €</strong>
                          {renderVariation(calculateVariation(panierMoyenPublicitaire(totals), panierMoyenPublicitaire(prevTotals)))}
                        </td>
                      )}
                      {isComponentMetricActive('valeur_conv') && (
                        <td className="numeric">
                          <strong>{formatNumber(totals.conversionsValue, 2)} €</strong>
                          {renderVariation(calculateVariation(totals.conversionsValue, prevTotals.conversionsValue || 0))}
                        </td>
                      )}
                      {isComponentMetricActive('cos') && (
                        <td className={`numeric ${getCosClassName(totalMetrics.cos || 0, selectedAccount?.id || 'global', moduleId)}`}>
                          <strong>{formatNumber(totalMetrics.cos, 1)}%</strong>
                          {renderVariation(calculateVariation(totalMetrics.cos, prevTotalMetrics.cos), true)}
                        </td>
                      )}
                      {isComponentMetricActive('roas') && (() => {
                        const roas = totals.cost > 0 ? (totals.conversionsValue / totals.cost) * 100 : 0;
                        const prevRoas = prevTotals.cost > 0 ? (prevTotals.conversionsValue / prevTotals.cost) * 100 : 0;
                        return (
                          <td className={`numeric ${getMetricObjectiveClassName('roas', roas, moduleId, selectedAccount?.id || 'global')}`}>
                            <strong>{roas >= 0 ? `${formatNumber(roas, 0)}%` : '-'}</strong>
                            {renderVariation(calculateVariation(roas, prevRoas))}
                          </td>
                        );
                      })()}
                      {isComponentMetricActive('roi') && (() => {
                        const hasData = totals.cost > 0 && totals.conversionsValue > 0;
                        const roi = hasData ? (totals.conversionsValue - totals.cost) / totals.cost : 0;
                        const prevHasData = prevTotals.cost > 0 && prevTotals.conversionsValue > 0;
                        const prevRoi = prevHasData ? (prevTotals.conversionsValue - prevTotals.cost) / prevTotals.cost : 0;
                        return (
                          <td className={`numeric ${getMetricObjectiveClassName('roi', roi, moduleId, selectedAccount?.id || 'global')}`}>
                            <strong>{formatNumber(roi, 2)}</strong>
                            {renderVariation(calculateVariation(roi, prevRoi))}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId="msads_networks"
          accountId={selectedAccount?.id || 'global'}
          title="Réseaux Microsoft Ads"
          hideApplyToAll={true}
        />
      )}
    </div>
  );
});

NetworksTable.displayName = 'NetworksTable';

export default NetworksTable;
